/**
 * HostAlertsToggle — enables Web Push notifications on the host phone.
 * Shows a single button. Handles: permission, subscription, unsubscribe,
 * and a "send test alert" button once enabled.
 */
import { useEffect, useState } from "react";
import { BellRinging, BellSlash, Check } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";

// Convert URL-safe base64 VAPID key to Uint8Array (browser PushManager wants raw bytes).
function urlB64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export default function HostAlertsToggle() {
  const [supported, setSupported] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setSupported(false);
      return;
    }
    (async () => {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub && Notification.permission === "granted");
    })();
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      if (Notification.permission === "denied") {
        toast.error("Notifications are blocked. Enable them in your phone's browser settings.");
        setBusy(false);
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        toast.error("Alerts not enabled — you'll need to allow notifications.");
        setBusy(false);
        return;
      }
      const { data: vapid } = await api.get("/push/vapid-public-key");
      if (!vapid?.public_key) {
        toast.error("Alerts server not configured yet — try again in a minute.");
        setBusy(false);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(vapid.public_key),
      });
      const raw = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: raw.endpoint,
        keys: raw.keys,
        device_label: navigator.userAgent.slice(0, 80),
      });
      setSubscribed(true);
      toast.success("Alerts enabled on this device.");
    } catch (e) {
      console.error("Push subscribe failed:", e);
      toast.error("Couldn't enable alerts. Try adding BuzzerAI to your home screen first.");
    }
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete(`/push/unsubscribe?endpoint=${encodeURIComponent(sub.endpoint)}`);
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Alerts turned off on this device.");
    } catch (e) {
      console.error("Push unsubscribe failed:", e);
      toast.error("Couldn't turn off alerts.");
    }
    setBusy(false);
  };

  const sendTest = async () => {
    try {
      await api.post("/push/test");
      toast.success("Test alert sent — check your notifications.");
    } catch (e) {
      toast.error("Test alert failed.");
    }
  };

  if (!supported) {
    return (
      <div className="paper-card p-4 flex items-center gap-3" data-testid="host-alerts-unsupported">
        <BellSlash size={20} color="#5C6B61" weight="fill" />
        <div className="text-sm text-[#5C6B61]">
          Locked-screen alerts aren't supported on this browser. Add BuzzerAI to your home screen from Safari (iPhone) or Chrome (Android) to enable them.
        </div>
      </div>
    );
  }

  return (
    <div className="paper-card p-4 flex items-center justify-between gap-3" data-testid="host-alerts-toggle">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${subscribed ? "bg-[#1E3A2F]" : "bg-[#E1DFD5]"}`}>
          <BellRinging size={20} color={subscribed ? "#F5C543" : "#5C6B61"} weight="fill" />
        </div>
        <div>
          <div className="font-semibold text-sm text-[#121914] flex items-center gap-1.5">
            Locked-screen alerts
            {subscribed && <Check size={14} color="#1E3A2F" weight="bold" />}
          </div>
          <div className="text-xs text-[#5C6B61]">
            {subscribed
              ? "This phone will chime when a guest joins or an order lands."
              : "Chime the host phone the moment a guest joins — even with the screen off."}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {subscribed && (
          <button
            type="button"
            onClick={sendTest}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded-full border border-[#E1DFD5] text-[#5C6B61] hover:bg-[#E1DFD5]"
            data-testid="host-alerts-test"
          >
            Send test
          </button>
        )}
        <button
          type="button"
          onClick={subscribed ? disable : enable}
          disabled={busy}
          className={`text-xs font-semibold px-4 py-1.5 rounded-full ${subscribed ? "bg-[#E1DFD5] text-[#5C6B61] hover:bg-[#D96C5B]/10 hover:text-[#D96C5B]" : "bg-[#1E3A2F] text-white hover:bg-[#2b5648]"}`}
          data-testid="host-alerts-btn"
        >
          {busy ? "…" : subscribed ? "Turn off" : "Enable"}
        </button>
      </div>
    </div>
  );
}
